import { Check, Crown, ExternalLink, RefreshCw, Users, Zap } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

interface PlanOption {
  id: string
  name: string
  price: number
  currency: string
  billing_period: 'monthly' | 'yearly'
  features: string[]
  limits: {
    events_per_month: number | null
    seats: number | null
    webhooks: number | null
    api_calls_per_day: number | null
  }
  popular?: boolean
}

interface PlanChangeDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentPlanId?: string
  onPlanSelected?: (planId: string) => void
}

export function PlanChangeDrawer({
  open,
  onOpenChange,
  currentPlanId,
  onPlanSelected,
}: PlanChangeDrawerProps) {
  const [loading, setLoading] = useState(false)
  const [changingPlan, setChangingPlan] = useState<string | null>(null)

  // Mock plan data - in real app this would come from props or API
  const plans: PlanOption[] = [
    {
      id: 'payg',
      name: 'Pay-as-you-go',
      price: 0.0,
      currency: 'USD',
      billing_period: 'monthly',
      features: [
        'Generous free monthly tier',
        'Usage-based pricing',
        'Standard support',
      ],
      limits: {
        events_per_month: null,
        seats: null,
        webhooks: null,
        api_calls_per_day: null,
      },
    },
  ]

  const _formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const _getPlanIcon = (planName: string) => {
    if (planName.toLowerCase().includes('team')) {
      return <Crown className='w-5 h-5 text-purple-600' />
    }
    if (planName.toLowerCase().includes('pro')) {
      return <Zap className='w-5 h-5 text-blue-600' />
    }
    return <Users className='w-5 h-5 text-green-600' />
  }

  const handleSelectPlan = async (planId: string) => {
    if (planId === currentPlanId) return

    setChangingPlan(planId)
    setLoading(true)

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      onPlanSelected?.(planId)
      onOpenChange(false)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setChangingPlan(null)
      setLoading(false)
    }
  }

  const getButtonText = (plan: PlanOption) => {
    if (changingPlan === plan.id) {
      return (
        <>
          <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
          Switching...
        </>
      )
    }

    if (plan.id === currentPlanId) {
      return 'Current plan'
    }

    return 'Upgrade to Pay-as-you-go'
  }

  const _getButtonVariant = (plan: PlanOption) => {
    if (plan.id === currentPlanId) {
      return 'secondary'
    }
    return 'default'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-5xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <div className='flex items-center justify-between'>
            <div>
              <DialogTitle className='text-md font-bold'>
                Change plan
              </DialogTitle>
            </div>
            <Button variant='ghost' size='sm' className='gap-2 pr-4'>
              <ExternalLink className='w-4 h-4' />
              Pricing
            </Button>
          </div>
        </DialogHeader>

        <div className='mt-8 flex justify-center'>
          {plans.map(plan => (
            <Card
              key={plan.id}
              className={`relative w-full max-w-md border-gray-200 ${
                plan.id === currentPlanId ? 'bg-gray-50' : ''
              }`}
            >
              <CardHeader className='pb-4'>
                <div className='space-y-3'>
                  <div>
                    <CardTitle className='text-xl font-bold'>
                      {plan.name}
                    </CardTitle>
                    <p className='text-sm text-muted-foreground'>
                      Usage-based pricing after free tier
                    </p>
                  </div>

                  <div className='space-y-1'>
                    <div className='text-sm text-muted-foreground'>
                      Starts at
                    </div>
                    <div className='text-2xl font-bold'>
                      $0
                      <span className='text-sm font-normal text-muted-foreground'>
                        /mo
                      </span>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className='space-y-4'>
                <div className='space-y-3'>
                  {plan.features.map((feature, index) => {
                    const getFeatureDisplay = (feat: string) => {
                      if (feat === 'Generous free monthly tier') {
                        return {
                          title: 'Generous free monthly tier',
                          subtitle:
                            plan.id === 'free'
                              ? 'Usage capped at free tier limits'
                              : 'Pay nothing if below free tier limits',
                        }
                      }
                      if (feat === 'Like, totally free') {
                        return {
                          title: 'Like, totally free',
                          subtitle:
                            'Almost all product features, no credit card required',
                        }
                      }
                      if (feat === 'Community support') {
                        return {
                          title: 'Community support',
                          subtitle: 'Support via community forum',
                        }
                      }
                      if (feat === 'Usage-based pricing') {
                        return {
                          title: 'Usage-based pricing',
                          subtitle:
                            'Set billing limits per product so you never pay more than expected.',
                        }
                      }
                      if (feat === 'Standard support') {
                        return {
                          title: 'Standard support',
                          subtitle:
                            'Support via email, Slack-based over $2k/mo',
                        }
                      }
                      return { title: feat, subtitle: null }
                    }

                    const { title, subtitle } = getFeatureDisplay(feature)

                    return (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                        key={index}
                        className='flex items-start gap-2 text-sm'
                      >
                        <Check className='w-4 h-4 text-green-600 mt-0.5 flex-shrink-0' />
                        <div>
                          <div className='font-medium'>{title}</div>
                          {subtitle && (
                            <div className='text-xs text-muted-foreground mt-1'>
                              {subtitle}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Additional info for Pay-as-you-go plan */}
                <div className='space-y-2 pt-2 border-t text-sm'>
                  <div className='font-medium'>3 Environments</div>
                  <div className='font-medium'>5-year data retention</div>
                </div>

                <Button
                  className='w-full bg-orange-500 hover:bg-orange-600 text-white'
                  variant='default'
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loading || plan.id === currentPlanId}
                >
                  {getButtonText(plan)}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className='mt-8 text-center text-sm text-muted-foreground'>
          <p>
            All plans include our core features. Upgrade or downgrade at any
            time.
            <br />
            Questions?{' '}
            <span className='text-blue-600 cursor-pointer hover:underline'>
              Contact our team
            </span>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
